<template>
    <section class="mentor-profile-editor">
        <div class="working" v-show="working">
            <div class="sk-folding-cube">
                <div class="sk-cube1 sk-cube"></div>
                <div class="sk-cube2 sk-cube"></div>
                <div class="sk-cube4 sk-cube"></div>
                <div class="sk-cube3 sk-cube"></div>
            </div>
        </div>
        <div class="panel" v-if="profile">
            <div class="panel-heading">
                <h2>Mentor Profile</h2>
            </div>
            <div class="panel-body archived" v-if="profile.archived">
                <h4>Your Mentor Profile has been archived. Please contact staff if you think this is an error.</h4>
            </div>
            <div class="panel-body" v-else>
                <p class="instructions">Please fill out all of the tabs. A <i class="fa fa-exclamation"></i> sign will appear next to incomplete tabs.</p>
                <p class="motivation" v-if="profileIncomplete">Mentors with a full profile have a 2.5x greater chance of connecting with students</p>
                <p class="instructions">
                        <span v-if="profileIncomplete" class="danger bold">Profile Incomplete</span>
                        
                        <span v-else>
                            <span v-if="unsavedWork" class="blue">Profile ready</span>
                            <span v-else class="green">Profile published!</span>
                        </span>
                    </p>
                <div class="tab-container">

                   <ul class="tabs clearfix">
                       <li v-for="tab in tabs" @click="selectedTab = tab" :class="{'active': selectedTab === tab}">
                            {{tab}}
                            <i v-if="tab === 'Bio' && bioIncomplete" class="fa fa-exclamation danger"></i>
                            <i v-if="tab === 'Availability' && !profile.user.timeZone" class="fa fa-exclamation danger"></i>
                            <i v-if="tab === 'Work Information' && workInfoIncomplete" class="fa fa-exclamation danger"></i>
                            <i v-if="tab === 'Software' && 
                                (!profile.mentor_software_proficiencies.length || !profile.mentor_software_proficiencies.find(p => p.proficiencyLevel > 0))" 
                                class="fa fa-exclamation danger"></i>
                           <i v-if="tab === 'Skills' && 
                                (!profile.mentor_skill_proficiencies.length|| !profile.mentor_skill_proficiencies.find(p => p.proficiencyLevel > 0))" 
                                class="fa fa-exclamation danger"></i>
                            <i v-if="tab === 'Previous Mentees' && roles.includes('Mentor') && mentees.length > 2 && (!selectedMentees || !selectedMentees.length)"
                                class="fa fa-exclamation danger"
                                >
                            </i>
                            <i v-if="tab === 'Work Examples' && (!profile.gallery || profile.gallery.length < 4 || !profile.credits || profile.credits.length < 4)" class="fa fa-exclamation danger"></i>

                       </li>
                   </ul>

                   <div v-show="selectedTab === 'Bio'" class="bio row">
                        <div class="section-header"><h3>A Short Bio&hellip;</h3></div>
                        <div class="image col-xs-3">
                            <img v-if="profile.featuredImage" :src="profile.featuredImage" class="">
                            <div class="featured-image-controls" :class="{'bold danger': !profile.featuredImage}">
                            Featured Artwork <label class="" for="featuredImage">Upload</label>
                                <input @change="upload($event, 'featuredImage')" class="file" type="file" id="featuredImage">
                            </div>
                            
                            <div v-if="roles.includes('Mentor')" class="flex demo-reel-url">
                                <label>Demo Reel URL</label>
                                <input 
                                    type="text" 
                                    class="demoreel" 
                                    v-model="profile.demoReelUrl" 
                                    @change="unsavedWork = true" 
                                    @paste="unsavedWork = true" 
                                    @keyup="unsavedWork = true" 
                                    placeholder="Vimeo or YouTube URL">
                            </div>                            
                            
                            <ul class="social-sites">
                                <li v-for="platform in socialPlatforms">
                                    <label>{{platform}}</label>
                                    <input @keyup="unsavedWork = true" @change="unsavedWork = true" type="text" v-model="socialSites[platform]" />
                                </li>
                            </ul>
                        </div>
                        <div class="text col-xs-9">

                            <label :class="{'bold danger': !profile.bio || profile.bio.trim() === '' || profile.bio.length > 500}">A Short Bio ({{bioCharCount}} / 500 char)</label>
                            
                            <textarea 
                                @change="unsavedWork = true" 
                                @paste="unsavedWork = true" 
                                @keyup="unsavedWork = true" 
                                v-model="profile.bio"></textarea>                                                   
                            
                            <div class="show-email">
                                <div>
                                    Show my email address on my profile
                                </div>
                                <div>
                                    <toggle-button @change="unsavedWork = true" v-model="profile.publicEmail"></toggle-button>
                                </div>
                            </div>
                            <label>I speak fluent:</label>
                            <ul class="unstyled languages">
                                <li v-for="language in languages">
                                    <label class="cursor-pointer">
                                        <input type="checkbox" @change="unsavedWork = true" v-model="profile.user.languages" :value="language">                                        
                                        {{language}}
                                    </label>
                                </li>
                            </ul>
                        </div>
                   </div>

                   <div v-show="selectedTab === 'Work Information'">
                    <div class="section-header"><h3>Work Information</h3></div>
                        <div class="work-information well">
                            <div class="inner">
                                <div class="line">
                                    <span :class="{'bold danger': !profile.company}">I work for</span> 
                                    <input @change="unsavedWork = true" 
                                    @paste="unsavedWork = true" 
                                    @keyup="unsavedWork = true"
                                    v-model.trim="profile.company" 
                                    type="text">
                                    <span :class="{'bold danger': !profile.companyUrl}">Company URL</span> 
                                    <input @change="unsavedWork = true" 
                                    @paste="unsavedWork = true" 
                                    @keyup="unsavedWork = true"
                                    v-model.trim="profile.companyUrl" type="text" placeholder="http://etc"><br>
                                </div>
                                <div class="line">
                                    <span :class="{'bold danger': !profile.position}">My current position/title is</span> 
                                    <input @change="unsavedWork = true" 
                                    @paste="unsavedWork = true" 
                                    @keyup="unsavedWork = true"
                                    type="text" 
                                    v-model.trim="profile.position"><br>
                                </div>
                                <div class="line">
                                    <span :class="{'bold danger': !profile.location}">I am currently living in</span> 
                                    <input @change="unsavedWork = true" 
                                    @paste="unsavedWork = true" 
                                    @keyup="unsavedWork = true"
                                    v-model.trim="profile.location" 
                                    type="text">
                                </div>
                                <div class="line">
                                    <span :class="{'bold danger': !profile.industry}">
                                        I am working in the
                                    </span>
                                    <select v-model="profile.industry" 
                                    @change="unsavedWork = true"                                     
                                    >
                                        <option value="" default selected>choose</option>
                                        <option value="vfx">VFX / Film & TV</option>
                                        <option value="video_games">Video Game & VR / AR</option>
                                        <option value="both">Both VFX and Games</option>
                                    </select>
                                    Industry
                                </div>  

                                 
                            </div>                         
                        </div>

                        <div class="specialties well">
                            <div class="inner">
                                <label :class="{'bold danger': !profile.specialties || !profile.specialties.length}">I am comfortable with:</label>
                                 <ul class="specialties">
                                    <li v-for="specialty in specialties">
                                        <input 
                                            :checked="profile.specialties.indexOf(specialty) > -1" 
                                            type="checkbox" 
                                            :id="specialty" 
                                            :value="specialty" 
                                            @change="setSpecialties(specialty); unsavedWork = true">
                                        <label :for="specialty">
                                            {{specialty | deslugify}}
                                        </label>
                                    </li>
                                </ul>
                            </div>
                        </div>
                        <div v-if="nextTermVenue !== 'campus'" class="specialties well">
                            <div class="inner">
                                <div>
                                    <label>Student Learning Path(s) Applicable to your Experience:</label>
                                    <ul class="specialties">
                                        <li v-for="path in mentorPaths">                                            
                                            <input 
                                                :checked="profile.paths.includes(path.id)" 
                                                type="checkbox" 
                                                :id="'path_' + path.id" 
                                                :value="path.name" 
                                                @change="setPaths(path)">
                                            <label :for="'path_' + path.id">
                                                {{path.name}}
                                            </label>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                   </div>

                   <div v-show="selectedTab === 'Availability'">
                    <div class="section-header"><h3>Work Availability</h3></div>
                    <div class="availability-table well">
                        <div class="inner">
                            <div class="time-availability">
                                <div class="mentor-timezones">
                                    <label :class="{'bold danger': !profile.user.timeZone}">Select your time zone:</label>
                                    <br><label class="bold">Please pick the major city closest to you, in your time zone.</label>
                                      <v-select
                                        :options="timeZoneResults"                                    
                                        v-model="profile.user.timeZone"
                                        label="label"
                                        index="value"    
                                        :get-option-label="getOptionLabel"                                    
                                        @search="searchTimeZones"
                                        @change="unsavedWork = true"
                                        @keyup.native="handleEmptyTimeZoneSearch"
                                        placeholder="Time Zone">
                                        <template slot="no-options">
                                            Begin typing...
                                        </template>
                                    </v-select>
                                    
                                </div>
                                <div class="line" v-if="roles.includes('Mentor')">
                                        <span :class="{'bold danger': !profile.availableDays}">
                                            I prefer to connect with my mentees on 
                                        </span>
                                        <select @click="unsavedWork = true" v-model="profile.availableDays" required>
                                            <option value="" default selected>choose</option>
                                            <option value="weekdays">week days</option>
                                            <option value="weekends">weekends</option>
                                            <option value="both">any time of the week</option>
                                        </select>
                                        <span :class="{'bold danger': !profile.availableTimes}">
                                            during the
                                        </span>
                                        <select @click="unsavedWork = true" v-model="profile.availableTimes" required>
                                            <option value="" default selected>choose</option>
                                            <option value="mornings">mornings</option>
                                            <option value="afternoons">afternoons</option>
                                            <option value="evenings">evenings</option>
                                            <option value="any">anytime of the day</option>
                                        </select>
                                </div> 
                            </div>
                            
                            <div v-if="roles.includes('Mentor')">
                                <label>Set your term-by-term availability:<br><b><i>Changing your availability/seats in one term will set all future terms to the same availability/seats</i></b></label>
                                <div class="header">
                                    <div>Term</div>
                                    <div>Status</div>
                                    <div>Number of Mentees</div>
                                </div>
                            
                                <div v-for="availability in profile.mentor_availabilities.filter((a,i) => i < 3) " class="line">
                                    <div>
                                        {{availability.term.startDate | intakeDate}} ({{availability.venue | capitalize}})
                                    </div>
                                    <div>
                                        <select 
                                            class="form-control" 
                                            @change="validateAvailability($event, availability)"
                                            :class="{'disabled': termHasStarted(availability.term)}"
                                            :disabled="termHasStarted(availability.term)"
                                            :title="termHasStarted(availability.term) ? 'You cannot change the status of a term that has already started' : ''"
                                            v-model="availability.status">
                                            <option value="" default disabled selected>choose</option>
                                            <option value="unconfirmed" disabled>Unconfirmed</option>
                                            <option value="available">Available</option>
                                            <option value="unavailable">Unavailable</option>
                                        </select>
                                    </div>

                                    <div>
                                        <select 
                                            class="form-control" 
                                            @change="validateSeats($event, availability)"
                                            :class="{'disabled': termHasStarted(availability.term)}"
                                            :disabled="termHasStarted(availability.term)"
                                            :title="termHasStarted(availability.term) ? 'You cannot change the status of a term that has already started' : ''"
                                            v-if="availability.status === 'available' || availability.status === 'unconfirmed'"
                                            v-model="availability.seats">
                                            <option value="" default disabled selected>choose</option>
                                            <option value="1">1</option>
                                            <option value="2">2</option>
                                            <option value="3">3</option>
                                            <option v-if="canHaveFourSeats" value="4" disabled>4</option>
                                        </select>
                                    </div>  
                                </div> 
                            </div>
                        </div>
                    </div>
                   </div>

                   <div v-show="selectedTab === 'Software'">
                    <div class="section-header"><h3>Software</h3></div>
                        <div class="legend">
                            <p>*in the Mentor Selector they will be represented like this: </p> <span class="pill very-familiar">Very Familiar</span> <span class="pill familiar">Familiar</span><span class="pill unfamiliar">Unfamiliar</span></span>                            
                        </div>
                        <div class="col-xs-12">Are we missing any software? Email us at <a href="mailto:mentors@tttc.ca?Subject=Mentor+Selector+Software+Suggestion" target="_blank">mentors@tttc.ca</a> with your suggestions.</div>
                        <ul v-if="software" class="software">
                            <li v-for="program in software" 
                                @click.prevent="setSoftwareProficiency(program); unsavedWork = true"
                                :class="{
                                    'very-familiar': softwareProficiencies[program.id] && softwareProficiencies[program.id].proficiencyLevel === 2, 'familiar': softwareProficiencies[program.id] && softwareProficiencies[program.id].proficiencyLevel === 2,
                                    'familiar': softwareProficiencies[program.id] && softwareProficiencies[program.id].proficiencyLevel === 2, 'familiar': softwareProficiencies[program.id] && softwareProficiencies[program.id].proficiencyLevel === 1,
                                    'unfamiliar': !softwareProficiencies[program.id] || (softwareProficiencies[program.id] && softwareProficiencies[program.id].proficiencyLevel === 0) 
                                }"
                                class="pill">
                                {{program.name}}
                            </li>
                        </ul>
                        
                   </div>

                   <div v-show="selectedTab === 'Skills'">
                        <div class="section-header"><h3>Skills</h3></div>
                        <div class="legend">
                            <p>*in the Mentor Selector they will be represented like this: </p> <span class="pill very-familiar">Very Familiar</span> <span class="pill familiar">Familiar</span><span class="pill unfamiliar">Unfamiliar</span></span>
                        </div>
                        <div class="col-xs-12">Are we missing any skills? Email us at <a href="mailto:mentors@tttc.ca?Subject=Mentor+Selector+Skill+Suggestion" target="_blank">mentors@tttc.ca</a> with your suggestions.</div>
                        <ul v-if="skills" class="skills">
                            <li v-for="skill in skills" 
                                @click.prevent="setSkill(skill); unsavedWork = true"
                                :class="{
                                    'very-familiar': mentorSkills[skill.id] && mentorSkills[skill.id].proficiencyLevel === 2, 'familiar': mentorSkills[skill.id] && mentorSkills[skill.id].proficiencyLevel === 2,
                                    'familiar': mentorSkills[skill.id] && mentorSkills[skill.id].proficiencyLevel === 2, 'familiar': mentorSkills[skill.id] && mentorSkills[skill.id].proficiencyLevel === 1,
                                    'unfamiliar': !mentorSkills[skill.id] || (mentorSkills[skill.id] && mentorSkills[skill.id].proficiencyLevel === 0) 
                                }"
                                class="pill">
                                {{skill.name}}
                            </li>
                        </ul>
                        
                   </div>

                   <div v-show="selectedTab === 'Work Examples'">
                        <div class="section-header"><h3>Work Examples</h3></div>
                        
                        <div class="work-examples">
                            <div class="text-right">
                                <button class="btn btn-success" @click.prevent="updateProfile()">Save Profile</button>
                            </div>                            
                            <h5 class="bold text-center" :class="{'danger':!profile.gallery || profile.gallery.length < 4}">Personal or Professional Work<br>(4-8 images required)</h5>

                            <div class="image-uploader"
                                :class="{
                                    'dragged-over': draggedOver === 'gallery',
                                    'disabled': profile.gallery && profile.gallery.length >= 8
                                }"
                                @drop="draggedOver = null" 
                                @dragleave="draggedOver = null"
                                @dragover.prevent="draggedOver = 'gallery'"
                            >
                                <label>
                                    Drag 4-8 images here
                                    <input type="file" name="gallery" multiple @change="upload($event, 'gallery')" accept="image/jpeg,image/png">
                                </label>
                            </div>

                            <div class="text-center">Re-order / Remove Personal or Professional Work</div>

                            <draggable v-model="profile.gallery" class="work-gallery">
                                <div v-for="(image, $i) in profile.gallery" class="gallery-item">
                                    <img :src="image" />
                                    <i class="fa fa-ban blue cursor-pointer" @click.prevent="removeWorkImage('gallery', $i)"></i>
                                </div>
                            </draggable>

                            <h5 class="bold text-center" :class="{'danger':!profile.credits || profile.credits.length < 4}">Credits (Box Art / Posters)<br>(4-8 images required)</h5>
                            <div class="image-uploader"
                                :class="{
                                    'dragged-over': draggedOver === 'credits',
                                    'disabled': profile.credits && profile.credits.length >= 8
                                }"
                                @drop="draggedOver = null" 
                                @dragleave="draggedOver = null"
                                @dragover.prevent="draggedOver = 'credits'"
                            >
                                <label>
                                    Drag 4-8 images here
                                    <input type="file" name="credits" multiple @change="upload($event, 'credits')" accept="image/jpeg,image/png">
                                </label>
                            </div>
                            <div class="text-center">Re-order / Remove Credits (Box Art / Posters)</div>
                            <draggable v-model="profile.credits" class="work-gallery credits">
                                <div v-for="(image, $i) in profile.credits" class="gallery-item">
                                    <img :src="image" />
                                    <i class="fa fa-ban blue cursor-pointer" @click.prevent="removeWorkImage('credits', $i)"></i>
                                </div>
                            </draggable>


                        </div>
                    </div>

                   <div v-show="selectedTab === 'Previous Mentees'">
                        <div class="section-header"><h3>Previous Mentees</h3></div>
                        <ul class="past-students unstyled">
                            <li class="mentee"><h5>Displayed on Profile</h5></li>
                            <li v-for="(student, $i) in selectedMentees" class="mentee">
                                <span class="cursor-pointer mentee-name" @click.prevent="openFeaturedMentee === $i ? openFeaturedMentee = null : openFeaturedMentee = $i">
                                    {{student.name}}
                                    <i class="fa fa-caret-down" v-if="openFeaturedMentee === $i"></i>
                                    <i v-else class="fa fa-caret-right"></i>
                                    <button class="btn btn-danger" @click.prevent="removeMentee($i)">Remove Mentee from Profile</button>
                                </span> 
                                
                                <div class="info" v-if="openFeaturedMentee === $i">
                                    <div class="meta">
                                        <select class="form-control" v-model="student.path">
                                            <option value="">No career path set</option>
                                            <option v-for="path in paths" :value="path.id">Career Path - {{path.name}}</option>
                                        </select>
                                        <div class="links">
                                            <label class="blue bold">Term</label>
                                            <input @keyup="unsavedWork = true" type="text" v-model="student.term">
                                        </div>
                                        <div class="links">
                                            <label class="blue bold">Demo Reel</label>
                                            <input @keyup="unsavedWork = true" type="text" v-model="student.reel" placeholder="YouTube or Vimeo">
                                        </div>
                                        <div class="links">
                                            <label class="blue bold">Artstation</label>
                                            <input @keyup="unsavedWork = true" type="text" v-model="student.artstation">
                                        </div>
                                        <div class="links">
                                            <label class="blue bold">LinkedIn</label>
                                            <input @keyup="unsavedWork = true" type="text" v-model="student.linkedin">
                                        </div>
                                        <div class="links">
                                            <label class="blue bold">Email</label>
                                            <input @keyup="unsavedWork = true" type="text" v-model="student.email">
                                        </div>
                                        <div class="links" title="Please check with the student before making their email address public">
                                            <label class="blue bold">Email Public</label>
                                            <toggle-button @change="unsavedWork = true" v-model="student.publicEmail"></toggle-button>
                                        </div>                                                                           

                                        <div class="links">
                                            <label class="blue bold">Additional Notes</label>
                                            <textarea rows="5" @keyup="unsavedWork = true" v-model="student.notes"></textarea>
                                        </div>                                                                           
                                        <button @click.stop="updateProfile" v-if="openFeaturedMentee === $i" class="btn btn-success btn-xs">Save Profile</button>                                        
                                    </div>
                                    <div class="artwork">
                                        <h5>Title Image</h5>
                                        <div class="has-remove-button">
                                            <i class="fa fa-ban blue cursor-pointer" v-if="student.avatar" @click="student.avatar = null"></i>
                                            <img :src="student.avatar" v-if="student.avatar" class="title-image">
                                            <div 
                                            v-else 
                                            class="images-uploader" 
                                            :class="{'dragged-over': draggedOver === 'title-image'}"
                                            @drop.prevent="addTitleImage($event, student, $i); draggedOver = null" 
                                            @dragleave="draggedOver = null"
                                            @dragover.prevent="draggedOver = 'title-image'"
                                            >
                                                <span class="instructions">
                                                    Drop title image here
                                                </span>
                                            </div>
                                        </div>


                                        <h5>Additional Images</h5>
                                        <div 
                                            class="images-uploader" 
                                            :class="{'dragged-over': draggedOver === 'additional-images'}"
                                            @drop.prevent="addArtwork($event, student, $i); draggedOver = null" 
                                            @dragleave="draggedOver = null"
                                            @dragover.prevent="draggedOver = 'additional-images'"
                                            >
                                            <draggable v-model="student.images" class="additional-images">
                                                <li v-for="(image, $j) in student.images" draggable @dragstart="setDragData($event, student)">
                                                    <img :src="image" />
                                                    <i class="fa fa-ban blue cursor-pointer" @click.prevent="removeImage($i, $j)"></i>
                                                </li>
                                            </draggable>

                                            <span class="instructions" v-if="student.images.length < 4">
                                                Drag up to {{4 - student.images.length}} more image<span v-if="4 - student.images.length > 1">s</span> here
                                            </span>
                                        </div>
                                    </div>
                                </div>    
                                <div class="library" v-if="openFeaturedMentee === $i">
                                    <div v-if="!menteeArtwork || menteeArtwork.length === 0">
                                        <h6>No artwork found</h6>
                                    </div>
                                    <div v-else>
                                        <h5>Artwork Library</h5>
                                        <ul class="artwork-library selectable">
                                            <li v-for="image in menteeArtwork">
                                                <a :href="image.url" target="_blank"><i class="fa fa-external-link"></i></a>
                                                <v-lazy-image :src="image.url" :title="image.name" :draggable="true" @dragstart="setDragData($event, student)" />
                                            </li>                                        
                                        </ul>
                                    </div>
                                </div>                        
                            </li>
                        </ul>


                        <ul class="mentees unstyled">
                            <li class="mentee"><h5>All Previous Mentees</h5></li>
                            <li v-for="(mentee, $i) in mentees" > 
                                <div class="mentee" v-show="notSelected(mentee)">
                                    <span class="cursor-pointer mentee-name" @click.prevent="toggleOpen('openAllMentees', $i)">
                                        {{mentee.firstName}} {{mentee.lastName}}
                                        <i class="fa fa-caret-down" v-if="openAllMentees.includes($i)"></i>
                                        <i v-else class="fa fa-caret-right"></i>
                                        <div v-if="!isDisplayed(mentee)"><button class="btn btn-success bt-xs" @click.stop="addToProfile(mentee, $i)">Display on Profile</button></div>
                                    </span>
                                    
                                    <div v-if="openAllMentees.includes($i)" class="mentee-info">

                                        <p>Career Path: {{getPathName(mentee.path)}}</p>
                                        <p v-if="mentee.socialLinks && mentee.socialLinks['ArtStation']">Artstation: <a :href="mentee.socialLinks['ArtStation']" class="cursor-pointer" target="_blank">{{mentee.socialLinks['ArtStation']}}</a></p>


                                        <div v-if="mentee.artwork.length === 0 && (!mentee.cloudinaryArtwork || mentee.cloudinaryArtwork.length === 0)">
                                            <h6>No artwork found</h6>
                                        </div>
                                        <div v-else>
                                            <h5>Artwork Library</h5>
                                            <ul class="artwork-library">
                                                <li v-for="image in mentee.artwork">
                                                    <a :href="image.url" target="_blank">
                                                        <v-lazy-image :src="image.url" :title="image.name" loading="lazy" />
                                                    </a>
                                                </li>
                                                
                                            </ul>
                                        </div>
                                      
                                    </div>
                                </div>
                            </li>
                        </ul>
                   </div>

                </div>
                <div class="form-group row col-xs-12">
                    <button class="btn btn-success" @click.prevent="updateProfile()">Save Profile</button>
                    <router-link v-if="!profileIncomplete && !unsavedWork" class="profile-link btn btn-success" target="_blank" :to="{name: 'mentorProfile', params:{id: localStorage.user_id}}">
                        View Profile
                    </router-link> 
                    <button v-if="profileIncomplete && !unsavedWork" @click.stop class="bt btn-danger not-ready">Profile not ready</button>                  
                </div>
                <div v-if="unsavedWork" class="unsaved-work danger row col-xs-12">You have unsaved work</div>
           </div>
        </div>
    </section>
</template>
<script>
import aws from '../services/aws';
import mentors from '../services/mentors';
import terms from '../services/terms';
import paths from '../services/paths';
import cloudinary from '../services/cloudinary';
import stringUtil from '../utils/strings.js';

import Dropzone from 'vue2-dropzone';
import VLazyImage from 'v-lazy-image/v2';
import toastr from 'toastr';
import moment from 'moment';
const uuidV4 = require('uuid/v4');
import draggable from 'vuedraggable';
import { uniq, uniqBy } from 'lodash'



function addProtocol(url) {
    
    if (!url.includes('://')) {
        url = 'https://' + url;
    }
    if (url.includes('http://')) {
        url = url.replace('http://', 'https://');    
    }
    return url;
}




export default {
    data() {
        return {
            working:false,
            profile: null,
            unsavedWork: false,
            tabs:['Bio','Work Information','Availability','Software','Skills','Work Examples'],
            selectedTab: 'Bio',
            specialties: ["character_modeling", "props_modeling", "lighting", "texturing", "vehicles", "generalist", "creatures", "environments", "look_dev", "character_animation", "art_direction", "compositing", "technical_director", "concept_art", "matte_painting"],
            socialPlatforms: ['Artstation', 'IMDB', 'LinkedIn', 'Instagram', 'Personal Website'],
            socialSites: {},
            terms: null,
            software: null,
            softwareProficiencies: {},
            skills: null,
            mentorSkills: {},
            availabilities: [],
            changedAvailabilities: [],
            hasFourSeats: false,
            languages: ['French','Portuguese','Spanish','Mandarin','Cantonese','Hindi','Russian','Italian','German','Klingon'],
            // only time zones that Zoom recognizes
            timeZones: [{"label":"Adelaide","value":"Australia/Adelaide"},{"label":"Aden","value":"Asia/Aden"},{"label":"Algiers","value":"Africa/Algiers"},{"label":"Almaty","value":"Asia/Almaty"},{"label":"Amman","value":"Asia/Amman"},{"label":"Amsterdam","value":"Europe/Amsterdam"},{"label":"Anchorage","value":"America/Anchorage"},{"label":"Apia","value":"Pacific/Apia"},{"label":"Araguaina","value":"America/Araguaina"},{"label":"Athens","value":"Europe/Athens"},{"label":"Atlantic","value":"Canada/Atlantic"},{"label":"Auckland","value":"Pacific/Auckland"},{"label":"Azores","value":"Atlantic/Azores"},{"label":"Baghdad","value":"Asia/Baghdad"},{"label":"Bahrain","value":"Asia/Bahrain"},{"label":"Baku","value":"Asia/Baku"},{"label":"Bangkok","value":"Asia/Bangkok"},{"label":"Bangui","value":"Africa/Bangui"},{"label":"Beirut","value":"Asia/Beirut"},{"label":"Belgrade","value":"Europe/Belgrade"},{"label":"Berlin","value":"Europe/Berlin"},{"label":"Bogota","value":"America/Bogota"},{"label":"Brisbane","value":"Australia/Brisbane"},{"label":"Brussels","value":"Europe/Brussels"},{"label":"Bucharest","value":"Europe/Bucharest"},{"label":"Budapest","value":"Europe/Budapest"},{"label":"Buenos_Aires","value":"America/Argentina/Buenos_Aires"},{"label":"Cairo","value":"Africa/Cairo"},{"label":"Calcutta","value":"Asia/Calcutta"},{"label":"Cape_Verde","value":"Atlantic/Cape_Verde"},{"label":"Caracas","value":"America/Caracas"},{"label":"Casablanca","value":"Africa/Casablanca"},{"label":"CET","value":"CET"},{"label":"Chicago","value":"America/Chicago"},{"label":"Copenhagen","value":"Europe/Copenhagen"},{"label":"Costa_Rica","value":"America/Costa_Rica"},{"label":"Dacca","value":"Asia/Dacca"},{"label":"Damascus","value":"Asia/Damascus"},{"label":"Darwin","value":"Australia/Darwin"},{"label":"Denver","value":"America/Denver"},{"label":"Dhaka","value":"Asia/Dhaka"},{"label":"Djibouti","value":"Africa/Djibouti"},{"label":"Dubai","value":"Asia/Dubai"},{"label":"Dublin","value":"Europe/Dublin"},{"label":"Edmonton","value":"America/Edmonton"},{"label":"El_Salvador","value":"America/El_Salvador"},{"label":"Fiji","value":"Pacific/Fiji"},{"label":"Godthab","value":"America/Godthab"},{"label":"Greenwich","value":"Etc/Greenwich"},{"label":"Guatemala","value":"America/Guatemala"},{"label":"Halifax","value":"America/Halifax"},{"label":"Harare","value":"Africa/Harare"},{"label":"Helsinki","value":"Europe/Helsinki"},{"label":"Hobart","value":"Australia/Hobart"},{"label":"Hong_Kong","value":"Asia/Hong_Kong"},{"label":"Honolulu","value":"Pacific/Honolulu"},{"label":"Indianapolis","value":"America/Indianapolis"},{"label":"Irkutsk","value":"Asia/Irkutsk"},{"label":"Istanbul","value":"Europe/Istanbul"},{"label":"Jakarta","value":"Asia/Jakarta"},{"label":"Jerusalem","value":"Asia/Jerusalem"},{"label":"Johannesburg","value":"Africa/Johannesburg"},{"label":"Kabul","value":"Asia/Kabul"},{"label":"Kamchatka","value":"Asia/Kamchatka"},{"label":"Kathmandu","value":"Asia/Kathmandu"},{"label":"Khartoum","value":"Africa/Khartoum"},{"label":"Kiev","value":"Europe/Kiev"},{"label":"Kolkata","value":"Asia/Kolkata"},{"label":"Krasnoyarsk","value":"Asia/Krasnoyarsk"},{"label":"Kuala_Lumpur","value":"Asia/Kuala_Lumpur"},{"label":"Kuwait","value":"Asia/Kuwait"},{"label":"Lima","value":"America/Lima"},{"label":"Lisbon","value":"Europe/Lisbon"},{"label":"London","value":"Europe/London"},{"label":"Los_Angeles","value":"America/Los_Angeles"},{"label":"Luxembourg","value":"Europe/Luxembourg"},{"label":"Madrid","value":"Europe/Madrid"},{"label":"Magadan","value":"Asia/Magadan"},{"label":"Managua","value":"America/Managua"},{"label":"Mazatlan","value":"America/Mazatlan"},{"label":"Mexico_City","value":"America/Mexico_City"},{"label":"Midway","value":"Pacific/Midway"},{"label":"Mogadishu","value":"Africa/Mogadishu"},{"label":"Montevideo","value":"America/Montevideo"},{"label":"Montreal","value":"America/Montreal"},{"label":"Moscow","value":"Europe/Moscow"},{"label":"Muscat","value":"Asia/Muscat"},{"label":"Nairobi","value":"Africa/Nairobi"},{"label":"New_York","value":"America/New_York"},{"label":"Nicosia","value":"Asia/Nicosia"},{"label":"Nouakchott","value":"Africa/Nouakchott"},{"label":"Noumea","value":"Pacific/Noumea"},{"label":"Novosibirsk","value":"Asia/Novosibirsk"},{"label":"Oslo","value":"Europe/Oslo"},{"label":"Pago_Pago","value":"Pacific/Pago_Pago"},{"label":"Panama","value":"America/Panama"},{"label":"Paris","value":"Europe/Paris"},{"label":"Perth","value":"Australia/Perth"},{"label":"Phoenix","value":"America/Phoenix"},{"label":"Port_Moresby","value":"Pacific/Port_Moresby"},{"label":"Prague","value":"Europe/Prague"},{"label":"Puerto_Rico","value":"America/Puerto_Rico"},{"label":"Qatar","value":"Asia/Qatar"},{"label":"Regina","value":"America/Regina"},{"label":"Reykjavik","value":"Atlantic/Reykjavik"},{"label":"Riyadh","value":"Asia/Riyadh"},{"label":"Rome","value":"Europe/Rome"},{"label":"Saigon","value":"Asia/Saigon"},{"label":"Santiago","value":"America/Santiago"},{"label":"Sao_Paulo","value":"America/Sao_Paulo"},{"label":"Seoul","value":"Asia/Seoul"},{"label":"Shanghai","value":"Asia/Shanghai"},{"label":"Singapore","value":"Asia/Singapore"},{"label":"Sofia","value":"Europe/Sofia"},{"label":"SST","value":"SST"},{"label":"St_Johns","value":"America/St_Johns"},{"label":"Stockholm","value":"Europe/Stockholm"},{"label":"Sydney","value":"Australia/Sydney"},{"label":"Taipei","value":"Asia/Taipei"},{"label":"Tashkent","value":"Asia/Tashkent"},{"label":"Tegucigalpa","value":"America/Tegucigalpa"},{"label":"Tehran","value":"Asia/Tehran"},{"label":"Tijuana","value":"America/Tijuana"},{"label":"Tokyo","value":"Asia/Tokyo"},{"label":"Tripoli","value":"Africa/Tripoli"},{"label":"Tunis","value":"Africa/Tunis"},{"label":"UTC","value":"UTC"},{"label":"Vancouver","value":"America/Vancouver"},{"label":"Vienna","value":"Europe/Vienna"},{"label":"Vladivostok","value":"Asia/Vladivostok"},{"label":"Warsaw","value":"Europe/Warsaw"},{"label":"Winnipeg","value":"America/Winnipeg"},{"label":"Yakutsk","value":"Asia/Yakutsk"},{"label":"Yekaterinburg","value":"Asia/Yekaterinburg"},{"label":"Zurich","value":"Europe/Zurich"}],
            timeZoneResults: [],
            mentees: [],
            selectedMentees: [],
            openFeaturedMentee: null,
            openAllMentees: [],
            paths:[],
            draggedOver: null,
            mentorPaths: [
                {"name": "Modeling for Film", "id": 6642},
                {"name": "Characters & Creatures for Film", "id": 9130},
                {"name": "Environments & Props for Film", "id": 9131},
                {"name": "Environments & Props for Games", "id": 6627},
                {"name": "Characters & Creatures for Games", "id": 6643},
                {"name": "Asset Creation for Film", "id": 6644},
                {"name": 'Not applicable', "id": 9999 }
            ]
        }
    },
    components: {
        draggable,
        VLazyImage
    },
    mounted() {
        this.getProfile();
        this.getSoftware();
        this.getSkills();
        
        this.getPaths();

        
        if (this.roles.includes('Mentor')) {
            this.tabs.push('Previous Mentees');
            this.getTerms();
        }
        this.timeZoneResults = this.timeZones;
        
        window.addEventListener('beforeunload', this.confirmExit);
        document.addEventListener('keydown', this.save);
        
    },
    destroyed() {
        window.removeEventListener('beforeunload', this.confirmExit);
        document.removeEventListener('keydown', this.save);
    },
    methods: {
       
        confirmExit(event) {
            if (this.unsavedWork) {
                event.returnValue = '';
            }
        },       
        getSoftware() {
            mentors.getSoftware().then((response)=>{
                this.software = response.data.software;
            });
        },
        getSkills() {
            mentors.getSkills().then((response)=>{
                this.skills = response.data.skills;
            });
        },
        getTerms() {
            const now = moment.utc().toDate();
            terms.get(this).then((response)=>{
                let currentAndFuture = response.data.terms.filter((t)=>{
                    return t.type === 'mentorship' && new Date(t.endDate) > now;
                }).sort((a,b)=>{
                    return new Date(a.startDate) - new Date(b.startDate);
                });

                let availableTerms = currentAndFuture.slice(0, 3);

                this.terms = availableTerms;
            });
        },
        getPaths() {            
            paths.getPaths().then((response)=>{                
                this.paths = response;                
            });
        },
        setPaths(path) {
            let index = this.profile.paths.findIndex(p => p === path.id);
            index === -1 ? this.profile.paths.push(path.id) : this.profile.paths.splice(index, 1)
        },
        getProfile() {      
            mentors.getProfile(this, localStorage.user_id).then((response) => {
                if (!response.data.profile) {
                    toastr.error('Mentor profile does not exist');
                    return false;
                }
                if (response.data.profile.archived) {
                    this.profile = response.data.profile;
                    return false;
                }

                if (!response.data.profile.paths) {
                    response.data.profile.paths = [];
                }
                if (!response.data.profile.user.languages) {
                    response.data.profile.user.languages = [];
                }
                this.profile = response.data.profile;

                for (const assessment of this.profile.mentor_software_proficiencies) {                    
                    this.softwareProficiencies[assessment.mentorSoftwareId] = assessment;
                }
                for (const assessment of this.profile.mentor_skill_proficiencies) {
                    this.mentorSkills[assessment.mentorSkillId] = assessment;   
                }

                if (this.roles.includes('Mentor')) {
                    this.selectedMentees = this.profile.pastMentoredStudents;                
                }

                for (const platform of this.socialPlatforms) {
                    const link = this.profile.socialSites.find((s) => s.site === platform);
                    if (link) {
                        this.socialSites[platform] = link.url;
                    } else {
                        this.socialSites[platform] = null;
                    }
                }
                const now = new Date();


                const canHaveFourSeats = this.profile.mentor_availabilities.find((a) => new Date(a.term.startDate > now) && a.seats === 4)
                
                this.canHaveFourSeats = canHaveFourSeats;


                this.$nextTick(()=>{
                    this.unsavedWork = false;
                    
                    if (this.roles.includes('Mentor')) {
                        this.getMentees();
                    }

                });
            });
        },
        getMentees() {
            mentors.getMentees().then((response)=>{
                this.mentees = response.data.data.map((s)=>{
                    
                    let artwork = [];
                    

                    const project = s.final_projects.find(p => p.assetImages && p.assetImages.find(a => stringUtil.isValidImage(a.url)));

                    if (project) {
                        const stillRenders = project.stillRenders
                                                .filter(r => stringUtil.isValidImage(r))
                                                .map((a) => { return {name: project.title, url: a } })

                        artwork = artwork.concat(stillRenders);

                        const assetImages = project.assetImages
                                    .filter(a => stringUtil.isValidImage(a.url))
                                    .map((a) => { return {name: project.title, url: a.url}});

                        artwork = artwork.concat(assetImages);  
                    }                      

                    

                    for (const post of s.gallery_posts) {
                        if (post.featuredMediaObj.type === 'image') {
                            artwork.push({name: post.title, url: post.featuredMediaObj.url});
                        }
                    }
                    
                    for (const submission of s.assignment_submissions) {
                        let urls = [];
                        if (submission.url) {
                            urls = submission.url.filter((u)=>{
                                return stringUtil.isValidImage(u);
                            })
                            .map((u)=>{
                                return { name: submission.assignment.name, url: u }
                            })
                        }
                        
                        artwork = artwork.concat(urls);
                        
                    }
                    if (s.extra_assets && s.extra_assets.length) {
                        
                        const extraAssets = s.extra_assets.filter(a => a.resource_type === 'image').map((a)=>{                            
                            
                            return {
                                name: a.folder.split('/').pop(),
                                url: a.secure_url
                            }
                        });

                        artwork = artwork.concat(extraAssets);                    

                    }

                    s.artwork = uniqBy(artwork, 'url');
                    
                    delete s.final_projects;
                    delete s.gallery_posts;
                    delete s.assignment_submissions;
                    return s;
                });
            });
        },
       
        removeMentee(index)  {
            const yes = window.confirm('Are you sure you want to remove this mentee?');
            if (yes) {
                this.selectedMentees.splice(index, 1);
                this.openFeaturedMentee = null;
            }
        },
        save(e) {
          if (e.ctrlKey && e.key === 's') {
            e.preventDefault();
            this.updateProfile();
          }
        },
        isValidUrl(url) {
            return stringUtil.isValidUrl(url);
        },
        updateProfile() {

            var self = this;

            let promises = [];

            const profile = this.profile;


            if (profile.user.timeZone && typeof profile.user.timeZone === 'object') {                
                profile.user.timeZone = profile.user.timeZone.value;
            }

            console.log(profile.user.timeZone)
          
            
            let errors = [];

            const charLimitedFields = ['position','company','companyUrl','location'];

            for (const field of charLimitedFields) {
                if (profile[field] && profile[field].length > 200) {
                    errors.push('Please keep your ' + field + ' under 200 characters');
                }
            }

            const urlFields = ['companyUrl'];

            for (const field of urlFields) {
                if (profile[field] && !stringUtil.isValidUrl(profile[field])) {
                    errors.push('Please enter a valid URL for ' + field);
                }
            }
           
            for (const mentee of this.selectedMentees) {
                
                if (!mentee.path) {
                    errors.push('Please select a path for ' + mentee.name + ' on the Previous Mentees tab');
                }
                
                if (mentee['artstation']) {
                    if (!stringUtil.isValidUrl(mentee['artstation'])) {
                        errors.push('Please enter a valid ArtStation URL for ' + mentee.name + ' on the Previous Mentees tab');
                    } 
                    else if (!mentee.artstation.includes('artstation')) {
                        errors.push('Please change the link to ArtStation ' + mentee.name + ' on the Previous Mentees tab');  
                    }
                }

                if (mentee['linkedIn']) {
                    if (!stringUtil.isValidUrl(mentee['linkedIn'])) {
                        errors.push('Please enter a valid LinkedIn URL for ' + mentee.name + ' on the Previous Mentees tab');
                    } 
                    else if (!mentee.artstation.includes('linkedin')) {
                        errors.push('Please change the link to LinkedIn ' + mentee.name + ' on the Previous Mentees tab');  
                    }
                }

                if (mentee['reel']) {
                    if (!mentee['reel'].includes('youtube')  
                    && !mentee['reel'].includes('youtu.be')
                    && !mentee['reel'].includes('vimeo')) {
                        errors.push('Only YouTube and Vimeo links are supported for demo reels');                    
                    }
                }
                
                
                if (!mentee.avatar) {
                    errors.push('Please add a title image for ' + mentee.name);
                }
                if (!mentee.images || mentee.images.length === 0) {
                    errors.push('Please add at least one additional image for ' + mentee.name);
                }
            }
          
            profile.pastMentoredStudents = this.selectedMentees;

         

            if (!profile.bio) {
                errors.push('Please fill out your bio');
            }
            if (profile.bio) {
                if ( profile.bio.trim() === '') {
                    errors.push('Please fill out your bio');                
                }
                if (profile.bio.length > 500) {
                    errors.push('Your bio must be less than 500 characters');                    
                }
            }

            if (profile.demoReelUrl) {  
                if (!stringUtil.isValidUrl(profile.demoReelUrl)) {
                    errors.push('Please enter a valid demo reel URL');                    
                } 
                else {      
                    if (!profile.demoReelUrl.includes('youtube')  
                        && !profile.demoReelUrl.includes('youtu.be')
                        && !profile.demoReelUrl.includes('vimeo')) {
                        errors.push('Only YouTube and Vimeo links are supported for your demo reel');                    
                    }
                }
                profile.demoReelUrl = addProtocol(profile.demoReelUrl);
            }       

           
            const updatedSocialSites = [];

            for (const site of Object.keys(this.socialSites)) {                
                if(this.socialSites[site]) {
                    updatedSocialSites.push({
                        site: site,
                        url: this.socialSites[site]
                    });
                }
            }

            profile.socialSites = updatedSocialSites;

            for (const platform of profile.socialSites) {
                if (platform.url && !stringUtil.isValidUrl(platform.url)) {
                    errors.push('Please enter a valid URL for ' + platform.site);
                }
            }

            if (errors.length) {
                for (const error of errors) {
                    toastr.error(error,null,{
                       "positionClass": "toast-top-full-width",
                       "titleClass": "text-center bold",
                       "messageClass": "text-center bold",                       
                    });                
                }                
                return;
            }

            this.openFeaturedMentee = null;
            this.openAllMentees = [];

            const proficiencies = [];

            for (const key of Object.keys(this.softwareProficiencies)) {
                proficiencies.push(this.softwareProficiencies[key]);
            }
            profile.mentor_software_proficiencies = proficiencies;

            const skills = [];
            for (const key of Object.keys(this.mentorSkills)) {
                skills.push(this.mentorSkills[key]);
            }
            profile.mentor_skill_proficiencies = skills;           

            profile.changedAvailabilities = uniq(this.changedAvailabilities);

            mentors.updateProfile(this, profile).then((response)=>{
                response.data.success === true ? toastr.success('Success!') : toastr.error('Error updating profile');
                this.changedAvailabilities = [];
                this.getProfile();
                this.unsavedWork = false;
            }).catch((e)=>{
                toastr.error('Error updating profile');
                console.log(e)
            });
        },
        validateAvailability(event, availability) {           
            if (availability.status !== 'available' && this.profile.user.mentor_requests) {
                const activeConnection = this.profile.user.mentor_requests.find((r)=>{
                    return r.term == availability.term && r.approvedByStudent === true && r.approvedByMentor === true;
                });
                if (activeConnection) {
                    toastr.error('You have an approved connection in this term and cannot set your status to unavailable. Please contact staff if you need to change this.');
                    availability.status = 'available';
                } else {                    
                    this.unsavedWork = true;   
                    this.setAvailabilities(availability);

                }             
            } else {                
                this.unsavedWork = true;   
                this.setAvailabilities(availability);
            }
        },
        setAvailabilities(availability) {
            this.changedAvailabilities.push(availability.id);
            const cutoff = new Date(availability.term.startDate)
            for (const a of this.profile.mentor_availabilities) {
                if (new Date(a.term.startDate) >= cutoff ) {                    
                    a.status = availability.status;          
                    this.changedAvailabilities.push(a.id);      
                }
            }
        },
        validateSeats(event, availability) {             
            const activeConnections = this.profile.user.mentor_requests.filter((r)=>{
                return r.term == availability.term && r.approvedByStudent === true && r.approvedByMentor === true;
            }); 
            if (availability.seats < activeConnections.length) {
                toastr.error('You have ' + activeConnections.length + ' connections approved already for this term; you must leave at least ' + activeConnections.length + ' seats available. Please contact staff if you need to change this.');
                availability.seats = activeConnections.length;            
            } else {
                this.unsavedWork = true;
                this.setSeats(availability);
            } 
        },
        setSeats(availability) {
            this.changedAvailabilities.push(availability.id);
            const cutoff = new Date(availability.term.startDate)
            for (const a of this.profile.mentor_availabilities) {
                if (new Date(a.term.startDate) >= cutoff ) {
                    a.seats = availability.seats;            
                    this.changedAvailabilities.push(a.id);    
                }
            }
        },
        setSoftwareProficiency(program) {
            const existing = this.softwareProficiencies[program.id];
            if (!existing) {
                this.softwareProficiencies[program.id] = {
                    mentorId: this.profile.id,
                    mentorSoftwareId: program.id,
                    proficiencyLevel: 1,
                }
            } else {
                if (existing.proficiencyLevel === 0) {
                    existing.proficiencyLevel = 1;  
                } else {
                    existing.proficiencyLevel === 1 ? existing.proficiencyLevel = 2 : existing.proficiencyLevel = 0;            
                }
            }
            this.$forceUpdate();
        },
        setSkill(skill) {
            const existing = this.mentorSkills[skill.id];
            if (!existing) {
                this.mentorSkills[skill.id] = {
                    mentorId: this.profile.id,
                    mentorSkillId: skill.id,
                    proficiencyLevel: 1,
                }
            } else {
                if (existing.proficiencyLevel === 0) {
                    existing.proficiencyLevel = 1;
                } else {
                    existing.proficiencyLevel === 1 ? existing.proficiencyLevel = 2 : existing.proficiencyLevel = 0;            
                }
            }
            this.$forceUpdate();
        },
        setSpecialties(specialty) {
            let index = this.profile.specialties.indexOf(specialty);
            index === -1 ? this.profile.specialties.push(specialty) : this.profile.specialties.splice(index, 1)
        },
        toggleOpen(field, index) {
            const i = this[field].indexOf(index);
            i === -1 ? this[field].push(index) : this[field].splice(i, 1);  
         
        },
        getOptionLabel(option) {

            let timezone;

            if (typeof option === 'object') {
                timezone = this.timeZones.find(t => t.value === option.value);
            } else {
                timezone = this.timeZones.find(t => t.value === option);
            }

            if (timezone) {
                return timezone.label.replace(/_/g, ' ');
            }
            
        },
        searchTimeZones(searchTerm, setLoading) {
            setLoading(true);

            const results = this.timeZones.filter(t => {
                return t.label.replace(/_/g, ' ').toLowerCase().includes(searchTerm.toLowerCase())
            });
            this.timeZoneResults = results;
            setLoading(false);
        },
        termHasStarted(term) {
            return new Date(term.startDate) < moment.utc().toDate();
        },
        addToProfile(mentee, index) {

            const pastStudent = {
                name: mentee.firstName + ' ' + mentee.lastName,
                id: mentee.id,                
                path: mentee.path,
                images: [],
                avatar: null
            }
            if (mentee.socialLinks && mentee.socialLinks['ArtStation']) {
                pastStudent.urls = mentee.socialLinks['ArtStation'];
            }
            this.selectedMentees.push(pastStudent);   
            
            const i = this.openAllMentees.indexOf(index);
            if (i > -1) { this.openAllMentees.splice(i, 1); }

            this.openFeaturedMentee = this.selectedMentees.length - 1;        


            
        },
        isDisplayed(mentee) {
            let selected = this.selectedMentees.find((m)=>{
                return m.id === mentee.id;
            });

            if (!selected) {
                selected = this.selectedMentees.find((m)=>{
                    return m.name === mentee.firstName + ' ' + mentee.lastName;
                });
            }
            return selected;
        },
        getPathName(id) {
            const path = this.paths.find(p => p.id === id);
            if (path) {
                return path.name;
            }
        },
        notSelected(mentee) {
            return !this.profile.pastMentoredStudents.find((s)=>{
                return s.id == mentee.id || (s.name == mentee.firstname + ' ' + mentee.lastName)
            })
        },
        handleEmptyTimeZoneSearch(e) {
            if (e.target.value.length === 0) {
                this.timeZoneResults = [];
            }  
        },
        setDragData(e, user) {
            let url;
            let name;
            if (e.target.tagName === 'LI') {
                url = e.target.firstChild.src;
                name = user.name;
            } else {
                url = e.target.src;            
                name = user.firstName + ' ' + user.lastName;
            }
            e.dataTransfer.setData('url', url);        
            e.dataTransfer.setData('userId', user.id)
            e.dataTransfer.setData('name', name);
        },
        removeImage(studentIndex, imageIndex) {
            this.selectedMentees[studentIndex].images.splice(imageIndex, 1);        
        },
        addTitleImage(e, user, index) {   
            const url = e.dataTransfer.getData('url');
            const userId = e.dataTransfer.getData('userId');
            this.selectedMentees[index].avatar = url; 
            this.$forceUpdate();         
        },
        addArtwork(e, user, index) {

            if (user.images.length >=4) {
                // toastr.error('Maximum 4 images allowed');
                e.preventDefault();
                return false;
            }
            
            const url = e.dataTransfer.getData('url');
            
            const alreadyDropped = user.images.find(i => i === url);
            if (alreadyDropped) {
                toastr.error('Image already added');
                e.preventDefault();
                return false;
            }
            const userId = e.dataTransfer.getData('userId')
            this.selectedMentees[index].images.push(url);                       
        },

        removeWorkImage(type, index) {
            this.profile[type].splice(index, 1);
            toastr.info('Please click "Save" to save changes');
        },

        async upload(e, type) {
            
            const files = e.target.files;
         
            let valid = true;

            if (type !== 'featuredImage') {
                if ((this.profile[type].length + files.length) > 8) {
                    toastr.error('You can upload a maximum of 8 images here');
                    return false;                
                }
            }

            let errors = [];

            for (const file of files) {
                if (file.size > 800000) {
                    valid = false;
                    toastr.error(file.name + ' is over the maximum file size of 800kb');
                    continue;
                }
                if (file.type !== 'image/png' && file.type !== 'image/jpeg') {
                    valid = false;
                    toastr.error('Only PNG and JPEG files are supported');
                    continue;
                }
            }
            if (valid === false) { return false; }
            
            this.working = true;
            let bucket = '';
            
            switch (type) {
                case 'avatar':
                    bucket = 'ttto-mentor-avatars';
                    break;
                case 'credits':
                    bucket = 'ttto-mentor-credits';
                    break;
                case 'gallery':
                    bucket = 'ttto-mentor-gallery';
                    break;              
                case 'featuredImage':
                    bucket = 'ttto-mentor-featured-images';
                    break;
            }


            this.unsavedWork = true;

            for (const file of files) {
                const filename = uuidV4() + '.' + file.name.split('.').pop();            
                const signature = await aws.signature(file, filename, bucket);
                const presignedUrl = await aws.getPresignedUrl(self, bucket, filename, 'putObject');
                const upload = await aws.upload(file, filename, signature, bucket, presignedUrl.data);
                if (type === 'featuredImage') {
                    this.profile.featuredImage = upload.url;
                } else {
                    this.profile[type].push(upload.url);
                }
                
            }
            toastr.info('Please click "Save" to save your changes');
            
            this.working = false;

        },       
    },
    watch: {
        openFeaturedMentee: function(newVal, oldVal) {
            if (newVal === null) {
                return;
            }

            const selected = this.selectedMentees[newVal];
            if (!selected) { return; }

            
            const mentee = this.mentees.find((m)=>{
                return m.id == selected.id || m.firstName + ' ' + m.lastName === selected.name;
            });
      ``
        }
    },
    computed: {
        nextTermVenue() {
            if (this.profile && this.profile.mentor_availabilities) {
                
                const now = new Date();
                
                const nextTerm = this.profile.mentor_availabilities.find((a)=>{
                    return new Date(a.term.startDate) > now;
                });

                return nextTerm.venue;                
                
            }

        },
        roles() {
            return JSON.parse(localStorage.getItem('roles'));
        },
        bioCharCount() {
            if (!this.profile.bio || this.profile.bio.trim() === '') {
                return 0;
            }
            return this.profile.bio.length;
        },
        profileIncomplete() {
            if (this.bioIncomplete === true) {
                return true;
            }
            if (this.workInfoIncomplete === true) {
                return true;
            }
            if (!this.profile.mentor_software_proficiencies) {
                return true;
            }
            if (this.profile.mentor_software_proficiencies.length === 0) {
                return true;
            }
            if (!this.profile.mentor_software_proficiencies.find(p => p.proficiencyLevel > 0)) {
                return true;
            }
            if (this.profile.mentor_skill_proficiencies.length === 0) {
                return true;
            }
            if (!this.profile.mentor_skill_proficiencies.find(p => p.proficiencyLevel > 0)) {
                return true;
            }
            if (this.mentees.length >= 3 && (!this.selectedMentees || !this.selectedMentees.length)) {
                return true;
            }

            return false;
            
        },
        menteeArtwork() {
            if (this.openFeaturedMentee !== null) {
                const selected = this.selectedMentees[this.openFeaturedMentee];
                const mentee = this.mentees.find((m)=>{
                    return m.id == selected.id || m.firstName + ' ' + m.lastName === selected.name;
                });
                if (mentee) {
                    return mentee.artwork;
                } else {
                    return [];
                }
            } else {
                return []
            }
        },
        bioIncomplete() {
            if (this.profile) {
                if (!this.profile.bio || this.profile.bio.trim() === '' || !this.profile.featuredImage){ 
                    return true;
                }
                if (this.profile.bio.length > 500) {
                    return true;
                }
                
                
                return false;                

            }
        },
        workInfoIncomplete() {
            if (this.profile) {
                if (
                    !this.profile.company || this.profile.company.trim() === ''
                    || !this.profile.companyUrl || this.profile.companyUrl.trim() === ''
                    || !this.profile.position || this.profile.position.trim() === ''
                    || !this.profile.location || this.profile.location.trim() === ''
                    || !this.profile.industry                   
                    || !this.profile.specialties || this.profile.specialties.length === 0
                ){ return true; }
                
                if (this.roles.includes('Mentor')) {
                    if(!this.profile.availableDays || !this.profile.availableTimes) {
                        return true;
                    }
                }
                return false;                

            }
        }
    }

}

</script>
<style lang="scss" scoped>
.archived.panel-body {
    margin-top:2rem;
    padding:2rem!important;
}
button.not-ready {
    position:relative;
    padding:9px 20px;
    top:2px;
    cursor:not-allowed;
}
.blue {
    color: #3cf;
}

.bold {
    font-weight:600!important;    
}
.danger {
    color:red!important;
}

.green {
    color: #00A652;
}

.section-header {
    padding-left:1.6rem;
    h3 {
        font-size:18px;
        background:#545454!important;
        display:inline-block;
        border:1px solid #989697;
        padding:2px 1rem;
    }
}
.has-remove-button {
    position:relative;
    .fa-ban, .fa-remove {
        position:absolute;
        top:1rem;
        right:1rem;
        font-size:1.8rem;
    }
}
.show-email {
    display: flex;
    align-items:center;
    margin:1rem 0;
    .vue-js-switch {
        margin-left:1rem;
        .v-switch-core {
            background:#3cf!important;
        }
    }
}
.unsaved-work {
    margin-top:1rem;
}


h4 {
    color: #b0b0b0;
    font-weight: 300;
    font-size: 1.5em;
}

.tabs {
    user-select: none;
    li {
        min-width:60px;
        text-align:center;
    }
}

h4.bump {
    margin-bottom: 25px;
}

.motivation {
    padding: 0 1rem;
    font-style:italic;
}
p.instructions {
    padding:1rem;
    font-size:16px;    
}

.bio {
    padding:1rem;
  
    textarea {
        width: 100%;
        height:200px;        
    }
    img {
        margin-top:1rem;
        width:100%;
        height: 180px;
        object-fit: cover;
    }
    .featured-image-controls {
        display:flex;
        width:100%;
        align-items: center;
        margin-top: 1rem;
        label {
            margin-left:1rem;
            background: #3cf;
            color: #222;
            padding:.5rem;

        }

    }
    ul.languages {
        display:flex;
        flex-wrap:wrap;
        li {
            width:50%;
            
        }
    }
}

.demo-reel-url {
    margin-bottom: 1rem;
    input {
        width: 100%;
    }
}
ul.social-sites {
    li {
        display: flex;
        flex-direction: column;
        margin-bottom:1rem; 
    }
}

.mentor-timezones {
    margin-bottom:1rem;
    border-bottom: 2px solid #555;
}

.work-information, .time-availability {        
    input[type=text], select {
        padding:.25rem 1rem;
        border-radius: 0!important;
        margin:0 .5rem;                
    }
  
    .line {
        border-bottom: 2px solid #555;
        padding:1rem 0;        
    }
}

.time-availability {
    margin-bottom:2rem;
}

.work-information, div.specialties, .availability-table {
    border:1px solid #555;
    margin:2rem;
    .inner {
        padding:1rem;
        width:780px;
        margin:0 auto;
    }
}

.availability-table {
    .header {
        margin-top:1.5rem;
    }
    .header, .line {
        display:flex;
        align-items: center;
        div {
            width:33.3333%;
        }
    }
    .disabled {
        opacity:.5;
    }
    .line select.form-control {
        padding:10px!important;
        width:140px;
    }
    
}

ul.specialties {
    border:none;
    display: flex;
    flex-wrap: wrap;
    flex-direction: row;

    li {
        margin: 1rem;
        background: #1a1a1a;
        padding: 0;
        border: 1px solid #555;
    }

    label {
        padding: 5px 10px;
        margin: 0;
        margin-top: -3px;
        cursor: pointer;
        font-weight: 300 !important;
    }

    input[type="checkbox"] {
        display: none;
    }

    input[type="checkbox"]:checked+label {
        background: #ccc;
        color: #1a1a1a;
        font-weight: 700 !important;
    }
}


input[type=text], textarea, select {
    background: #000;        
    color: #fff;
    padding:1rem;
    border: 1px solid #555;
    border-radius:4px;
}
ul.software, ul.skills {
    padding: 1.4rem;
    display: flex;
    flex-wrap: wrap;    
    justify-content:center;
}

.work-examples {
    padding:2rem;
    h5 {
        line-height:1.4em;
    }
    .image-uploader {
        width:400px;
        border:2px solid #555;
        margin:0 auto 4rem;
        background:#000;
        &.dragged-over {
            background: #333;
        }
        &.disabled {
            opacity:.25;
        }
        label {
            display:flex;
            width:100%;
            height:200px;
            align-items:center;
            justify-content: center;
            color: #fff;
            font-weight:600!important;
        }
        input {
            opacity:0;
            position:absolute;
            top:0;
            left:0;
            right:0;
            bottom:0;            
            width:100%;
            height:100%;
        }
    }
}

.work-gallery {
    display:flex;
    flex-wrap:wrap;
    align-items:center;
    justify-content: center;
    margin-bottom: 3rem;

    &.credits {
        .gallery-item {
            width:200px;
            height:300px;
        }
    }
    .gallery-item {
        position: relative;
        margin:1rem;        
        width:320px;
        height:180px;
        i {
            position:absolute;
            top: 1rem;
            right: 1rem;
            font-size:1.8rem;
        }
        img {
            width:100%;
            height:100%;
            object-fit:cover;
        }
    }
}

.past-students, .mentees {
    margin: 1rem 1rem 2rem 1rem;
    border:1px solid #555;    
    .mentee {
        border-bottom:1px solid #555;
        padding:1rem 1.5rem;
    }
    .mentee:last-child {
        border-bottom: none;
    }
}

.mentee-info {
    margin-top:2rem;
}

.mentee-name {
    font-size:16px;
    display:flex; 
    align-items:center;
    i {
        margin:0 1rem;
    }
    .btn {        
        padding:3px 8px;
    }
}
.past-students {
    .info {
        display:flex;
        .meta {
            width:50%;
            padding-right:3rem;
            .links {
                display:flex;
                align-items:center;
                margin:2rem 0;
                label {
                    width:120px;
                }
                input {
                    margin-left:2rem;
                    width:320px;
                }
                .vue-js-switch {
                    margin-left:2rem;
                }
                textarea {
                    margin-left:1rem;
                }
                textarea {
                    width:100%;
                }
            }
        }
        .artwork {
            width:50%;
        }
    }
}
.images-uploader {
    background:#000;
    padding:1rem;
    min-height:120px;
    img {
        pointer-events: none;
    }
    &.dragged-over {
        background: #333;
    }
}
.title-image {
    width:100%;
    height: 400px;
    object-fit:cover;
}
.additional-images {
    display:flex;
    flex-wrap: wrap;    
    margin: 0 -1rem;
    img {
        height: 200px;
        width:100%;
        object-fit: cover;
    } 
    li {
        width:50%;
        padding:1rem;
        position:relative;
        .fa {
            position:absolute;
            top:1.4rem;
            right:1.4rem;
            font-size:1.8rem;
        }
    }
}


ul.artwork-library {
    display:flex;
    flex-wrap:wrap;
    max-height:600px;
    overflow-y: auto;
    margin-top: 2rem;
    li {
        padding:.5rem;
        width:33.3333%;
        display:flex;
        align-items:center;
        justify-content:center;
        img {
            width:100%;
            height:220px;
            object-fit:cover;
        }
    }
}
.artwork-library.selectable {
    li {
        position:relative;
        a {
            position:absolute;
            top:1rem;
            right:1rem;
            font-size:1.8rem;                                

        }
    }
    img {
        cursor: move;
    }
}

.pill {
    border: 1px solid #636162;
    border-radius: 4px;
    background: #000;
    color: #fff;
    padding: .5rem 1rem;
    margin-right: 1.4rem;
    margin-bottom: 1.4rem;
    cursor: pointer;
    user-select: none;
    &.very-familiar {
        background: #00A652;
    }
    &.familiar {
        background: #7D7D7D;   
    }
}

.legend {
    display:flex;
    align-items: center;
    padding:1.4rem 1.4rem 0 1.4rem;
    p {
        margin-right:2rem;
    }
}

form {
    padding: 2rem;
    font-size: 1.6rem;
    line-height: 3rem;

    input,
    textarea,
    button {
        background: #1a1a1a;
        color: #ededed;
        padding: 10px;
        margin: 0 1rem;
        border: 1px solid #555;
        line-height: initial;
        margin: 1rem;
    }

    textarea {
        height: 200px;
        width: 800px;
        margin-top: 1rem;
        font-weight: 100;
        font-size: .9em;
    }

    option {
        background: #000;
    }

    select {
        background: transparent;
        border: none;
        margin: 0 1rem;
        display: inline-block;
        border-bottom: 1px solid #555;
    }

    br {
        line-height: 6rem;
    }

    button[type="submit"],
    .profile-link {
        background: #3cf;
        color: #1a1a1a;
        float: right;
        padding: 1rem;
        margin-bottom: 4rem;
    }

    button[type="submit"] {
        height: 5rem;
        top: -10px;
        position: relative;
    }

    // .demoreel {
    //     width: 400px;
    // }
}

input.file {
    display: none;
}

label.file {
    background: #3cf;
    color: #000;
    font-weight: 600 !important;
    padding: 1rem 25px;
    margin: 0 1rem 0 1rem;
    border: 1px solid #555;
    cursor: pointer;
}


.dropzone {
    margin-top: 1rem;
    max-width: 400px;
    border: 1px solid #555;
}



.deletable {
    position: relative;

    .delete {
        position: absolute;
        top: 0;
        right: 0;
        cursor: pointer;
    }
}

.working {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(25, 25, 25, .6);
    z-index: 999999;
    display: flex;
    align-items: center;
    justify-content: center;
}

.sk-folding-cube {
    margin: 20px auto;
    width: 80px;
    height: 80px;
    position: relative;
    -webkit-transform: rotateZ(45deg);
    transform: rotateZ(45deg);
}

.sk-folding-cube .sk-cube {
    float: left;
    width: 50%;
    height: 50%;
    position: relative;
    -webkit-transform: scale(1.1);
    -ms-transform: scale(1.1);
    transform: scale(1.1);
}

.sk-folding-cube .sk-cube:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: #3cf;
    -webkit-animation: sk-foldCubeAngle 2.4s infinite linear both;
    animation: sk-foldCubeAngle 2.4s infinite linear both;
    -webkit-transform-origin: 100% 100%;
    -ms-transform-origin: 100% 100%;
    transform-origin: 100% 100%;
}

.sk-folding-cube .sk-cube2 {
    -webkit-transform: scale(1.1) rotateZ(90deg);
    transform: scale(1.1) rotateZ(90deg);
}

.sk-folding-cube .sk-cube3 {
    -webkit-transform: scale(1.1) rotateZ(180deg);
    transform: scale(1.1) rotateZ(180deg);
}

.sk-folding-cube .sk-cube4 {
    -webkit-transform: scale(1.1) rotateZ(270deg);
    transform: scale(1.1) rotateZ(270deg);
}

.sk-folding-cube .sk-cube2:before {
    -webkit-animation-delay: 0.3s;
    animation-delay: 0.3s;
}

.sk-folding-cube .sk-cube3:before {
    -webkit-animation-delay: 0.6s;
    animation-delay: 0.6s;
}

.sk-folding-cube .sk-cube4:before {
    -webkit-animation-delay: 0.9s;
    animation-delay: 0.9s;
}

@-webkit-keyframes sk-foldCubeAngle {

    0%,
    10% {
        -webkit-transform: perspective(140px) rotateX(-180deg);
        transform: perspective(140px) rotateX(-180deg);
        opacity: 0;
    }

    25%,
    75% {
        -webkit-transform: perspective(140px) rotateX(0deg);
        transform: perspective(140px) rotateX(0deg);
        opacity: 1;
    }

    90%,
    100% {
        -webkit-transform: perspective(140px) rotateY(180deg);
        transform: perspective(140px) rotateY(180deg);
        opacity: 0;
    }
}

@keyframes sk-foldCubeAngle {

    0%,
    10% {
        -webkit-transform: perspective(140px) rotateX(-180deg);
        transform: perspective(140px) rotateX(-180deg);
        opacity: 0;
    }

    25%,
    75% {
        -webkit-transform: perspective(140px) rotateX(0deg);
        transform: perspective(140px) rotateX(0deg);
        opacity: 1;
    }

    90%,
    100% {
        -webkit-transform: perspective(140px) rotateY(180deg);
        transform: perspective(140px) rotateY(180deg);
        opacity: 0;
    }
}

</style>
<style lang="scss">
.mentor-profile-editor {
    .dz-message span {
        font-family: 'Open Sans', sans-serif;
        font-size: 1.6rem;
        color: #b0b0b0
    }
    .vue-js-switch.toggled .v-switch-core {
        background:#3cf!important;
    }
    .mentor-timezones {
        .v-select {
            width:100%;
            .dropdown-toggle {
                border:none;
            }
            input[type=search] {
                border-bottom:none!important;
            }
            .dropdown-menu * {
                color:#fff!important;
                background:#333!important;
            }
            button {
                color: #fff!important;
            }
            i::before {
                border-color:#fff!important;
                position:relative;            
            }
            .selected-tag {
                border:none;
                color:#fff!important;
                border-bottom:none;
                border-radius:0;
                position:relative;
                top:1px;
                left:2px;
            }   
        }
    }
}


</style>



// WEBPACK FOOTER //
// MentorProfileEditorNew.vue?064fccd0